"use client";

import { Lookup, SpringRef, useChain, useSpringRef } from "@react-spring/web";
import React, { ReactElement, useEffect } from "react";
import { useSelector } from "react-redux";

import CookiePolicyButton from "@/app/shared-components/cookie-policy-button/cookie-policy-button";
import CountryChangeModal from "@/app/shared-components/country-change-modal/country-change-modal";
import QuestionFooterSection from "@/app/shared-components/question-footer-section";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import QMedicalQuestionFormContent from "@/components/organisms/question-section/medical/q-medical-question-form-content";
import QMedicalSupportContent from "@/components/organisms/question-section/medical/q-medical-support-content";
import { MedicalConfirmationFlowEnum } from "@/components/with-undiagnosed-symptoms";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useUpdateProgressBar from "@/hooks/progress/use-update-progress-bar";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import GatekeeperQuestionsProvider from "@/providers/gatekeeper-questions-provider";
import { useLoading } from "@/providers/loading-provider";
import { RootState } from "@/store/root-reducer";
import { GatekeeperQuestion } from "@/utils/schema/gatekeeper-question-page-schema.utils";

import withQuoteData from "../with-quote-data";

type FormComponentProps = {
  qMedicalContent: GatekeeperQuestion;
  newMedicalFlowEnabled: boolean;
  medicalConfirmationFlow?: MedicalConfirmationFlowEnum;
  undiagnosedQuestionExperimentEnabled?: boolean;
  routeName?: string;
  application?: "quote" | "renewal";
  formRef?: SpringRef<Lookup>;
  ctaRef?: SpringRef<Lookup>;
};

type SupportComponentProps = {
  qMedicalContent: GatekeeperQuestion;
  showondesktop?: string;
  routeName?: string;
  supportRef?: SpringRef<Lookup>;
  newMedicalFlowEnabled: boolean;
  medicalConfirmationFlow?: MedicalConfirmationFlowEnum;
};

type ClientComponentProps = {
  helpButton: ReactElement;
  logo: ReactElement;
  qMedicalContent: GatekeeperQuestion;
  newMedicalFlowEnabled: boolean;
  routeName: string;
  pageName: string;
  undiagnosedQuestionEnabled?: boolean;
  undiagnosedQuestionExperimentEnabled?: boolean;
  medicalConfirmationFlow?: MedicalConfirmationFlowEnum;
  FormComponent?: React.ComponentType<FormComponentProps>;
  SupportComponent?: React.ComponentType<SupportComponentProps>;
};

function GatekeeperWrapper({
  helpButton,
  logo,
  qMedicalContent,
  newMedicalFlowEnabled,
  routeName,
  pageName,
  undiagnosedQuestionEnabled,
  undiagnosedQuestionExperimentEnabled,
  medicalConfirmationFlow,
  FormComponent = QMedicalQuestionFormContent,
  SupportComponent = QMedicalSupportContent,
}: Readonly<ClientComponentProps>) {
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");
  const { showLoading, hideLoading } = useLoading();
  const { referenceId } = useSelector((state: RootState) => state.quote);

  const formContentSpringRef = useSpringRef();
  const formCtaSpringRef = useSpringRef();
  const supportContentSpringRef = useSpringRef();

  useChain(
    [formContentSpringRef, formCtaSpringRef, supportContentSpringRef],
    [0, 0.5, 1]
  );

  const { pageReferrer, pageUrl, pageTitle } = useCustomPageTracking(pageName);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  const { current, wizard } = useWizard();
  const { productSpecialFeatures } = wizard;
  useUpdateProgressBar(current?.progressBarPercentage ?? -1);

  useEffect(() => {
    if (!referenceId) {
      showLoading();
    } else {
      hideLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceId]);

  if (!isReady) return null;
  return (
    <GatekeeperQuestionsProvider
      undiagnosedQuestionEnabled={undiagnosedQuestionEnabled}
      undiagnosedQuestionExperimentEnabled={
        undiagnosedQuestionExperimentEnabled
      }
    >
      {productSpecialFeatures?.showCountryFooter && <CountryChangeModal />}
      <div
        data-testid="gatekeeper-wrapper-test-id"
        className=" bg-surface-neutral-option-2 "
      >
        <div className="max-w-[1200px] mx-auto my-0 w-[100%] relative min-h-[calc(100vh-12px)] lg:pb-0 pb-spacing-5xl ">
          <main className="md:pt-spacing-xl lg:pt-spacing-xxl md:pb-spacing-xxl pt-spacing-m pb-spacing-m ">
            <div
              className=" grid md:grid-cols-12 grid-cols-6 md:gap-spacing-l gap-spacing-m xl:mx-auto lg:mx-[72px] 
      md:mx-[48px] mx-[24px] relative"
            >
              <div className="lg:col-span-7 col-span-12 bg-surface-neutral-option-1 rounded-radius-xxl ">
                <div className=" flex flex-col h-full">
                  <div className="text-surface-neutral-option-2 relative">
                    {helpButton}
                    {logo}
                  </div>
                  <FormComponent
                    qMedicalContent={qMedicalContent}
                    newMedicalFlowEnabled={newMedicalFlowEnabled}
                    medicalConfirmationFlow={medicalConfirmationFlow}
                    undiagnosedQuestionExperimentEnabled={
                      undiagnosedQuestionExperimentEnabled
                    }
                    routeName={routeName}
                    formRef={formContentSpringRef}
                    ctaRef={formCtaSpringRef}
                  />
                </div>
              </div>
              <div className={`lg:col-span-5 col-span-12 md:mb-0 `}>
                <div
                  className="rounded-radius-xxl bg-dark-surface-accent-1-primary flex lg:flex-col
          flex-col-reverse text-surface-neutral-option-2 font-body-font h-full"
                >
                  <SupportComponent
                    qMedicalContent={qMedicalContent}
                    showondesktop={isdesktop.toString()}
                    routeName={routeName}
                    supportRef={supportContentSpringRef}
                    newMedicalFlowEnabled={newMedicalFlowEnabled}
                    medicalConfirmationFlow={medicalConfirmationFlow}
                  />
                </div>
              </div>
            </div>
            <QuestionFooterSection />
            {productSpecialFeatures?.useNewHelpOverlay && (
              <CookiePolicyButton />
            )}
          </main>
        </div>
      </div>
    </GatekeeperQuestionsProvider>
  );
}

export default withQuoteData(GatekeeperWrapper);
